import React from 'react';
import { App, Button, Tabs, Typography } from 'antd';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useParams } from 'react-router';
import { useMutation, useQuery } from '@apollo/client';
import { useProjectPhase } from '../hooks';
import { Loader } from '../../../components/UI/Loader';
import { mutations, queries } from '../../../graphql/graphql';
import type { ProposalPhaseData } from '../models/ProposalPhaseModel';
import { ProposalView } from '../../../components/Proposals/View';

interface ProjectProposalPageProps {
  phaseId?: string;
}

export const ProjectProposalPage: React.FC<ProjectProposalPageProps> = ({ phaseId }) => {
  const { phaseId: routePhaseId } = useParams();
  const resolvedPhaseId = phaseId || routePhaseId;
  const { message } = App.useApp();
  const { loading, phase, setData, savePhase } = useProjectPhase(resolvedPhaseId);
  const [proposalOrderCreate, { loading: isSubmitting }] = useMutation(
    mutations.order.PROPOSAL_ORDER_CREATE
  );

  const phaseData = (phase?.data || {}) as ProposalPhaseData;
  const projectId = phase?.projectId;
  const orderId = phaseData.orderId;

  const { data: proposalsData, loading: proposalsLoading } = useQuery(
    queries.proposal.ORDER_PROPOSALS,
    {
      variables: { orderId },
      skip: !orderId,
      fetchPolicy: 'network-only',
    }
  );

  const proposals = proposalsData?.orderProposals || [];
  const requestedCount = proposals.length;
  const respondedCount = proposals.filter((proposal: { status?: string }) => {
    return proposal.status && proposal.status !== 'WAITING';
  }).length;
  const hasResponses = respondedCount > 0;
  const respondedProposals = proposals.filter(
    (proposal: { status?: string }) => proposal.status === 'RESPONSES_RECEIVED'
  );
  const pendingProposals = proposals.filter(
    (proposal: { status?: string }) => proposal.status !== 'RESPONSES_RECEIVED'
  );
  const remainingCount = Math.max(requestedCount - respondedCount, 0);

  const handleRequestQuote = async () => {
    if (!projectId) {
      message.error('Project not found for this phase.');
      return;
    }

    try {
      const res = await proposalOrderCreate({ variables: { projectId } });
      const createdOrderId = res.data?.proposalOrderCreate?.id;
      if (createdOrderId) {
        const nextData: ProposalPhaseData = { ...phaseData, orderId: createdOrderId };
        setData(nextData);
        await savePhase({ data: nextData }, { omitIsActive: true });
      }
      message.success('Your request has been submitted.');
    } catch (error) {
      console.error('[ProjectProposalPage] proposalOrderCreate failed', error);
      message.error('Failed to submit your request. Please try again.');
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (!orderId) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          gap: 16,
          maxWidth: 720,
          margin: '0 auto',
        }}>
        <ClockCircleOutlined style={{ fontSize: 48, color: '#111' }} />
        <Typography.Title level={4} style={{ margin: 0 }}>
          Request a proposal from a local builder.
        </Typography.Title>
        <Typography.Paragraph style={{ margin: 0, color: '#4b4b4b' }}>
          Your proposal will be prepared based on your neighborhood, landscape, and the other key
          details you provided in the previous steps. By submitting this request, you confirm that
          everything is accurate, which helps us deliver a precise proposal for your home.
        </Typography.Paragraph>
        <Button type="primary" onClick={handleRequestQuote} loading={isSubmitting}>
          Get a Quote from the Builder
        </Button>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', padding: '24px 0' }}>
      <Tabs
        type="card"
        items={[
          ...respondedProposals.map(
            (
              proposal: { id: string; contractor?: { name?: string | null } | null },
              index: number
            ) => ({
              key: proposal.id,
              label: `GC ${index + 1}`,
              children: <ProposalView id={proposal.id} />,
            })
          ),
          ...pendingProposals.map(
            (
              proposal: { id: string; contractor?: { name?: string | null } | null },
              index: number
            ) => ({
              key: `${proposal.id}-pending`,
              label: (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <ClockCircleOutlined style={{ fontSize: 12, color: '#4b4b4b' }} />
                  {`GC ${respondedProposals.length + index + 1}`}
                </span>
              ),
              children: (
                <div style={{ textAlign: 'center', padding: '48px 16px' }}>
                  <ClockCircleOutlined style={{ fontSize: 48, color: '#111' }} />
                  <Typography.Title level={5} style={{ marginTop: 16 }}>
                    Proposal in progress
                  </Typography.Title>
                  <Typography.Paragraph style={{ margin: 0, color: '#4b4b4b' }}>
                    We have sent your request to the builder. We will reach out shortly to confirm the next step.
                  </Typography.Paragraph>
                </div>
              ),
            })
          ),
        ]}
      />
    </div>
  );
};
